/**
 * AlertModal - Reusable alert/notification dialog
 * Replaces native window.alert() with styled modal
 */

import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, CheckCircle, AlertCircle, XCircle, Info } from 'lucide-react';
import '../AIGarage.css';

const AlertModal = ({
  isOpen,
  title,
  message,
  buttonText = 'OK',
  onClose,
  variant = 'info' // 'success', 'error', 'warning', 'info'
}) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);

  // Handle ESC key to close modal
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Auto-close success messages after 3 seconds
  useEffect(() => {
    if (isOpen && variant === 'success') {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, variant, onClose]);

  // Don't render if modal is not open
  if (!isOpen) return null;

  const getIcon = () => {
    switch (variant) {
      case 'success':
        return <CheckCircle size={48} color="#10B981" />;
      case 'error':
        return <XCircle size={48} color="#DC2626" />;
      case 'warning':
        return <AlertCircle size={48} color="#F59E0B" />;
      default:
        return <Info size={48} color="#0684BC" />;
    }
  };

  const getTitle = () => {
    if (title) return title;
    
    switch (variant) {
      case 'success':
        return 'Success';
      case 'error':
        return 'Error';
      case 'warning':
        return 'Warning';
      default:
        return 'Information';
    }
  };

  const modalContent = (
    <div className="garage-modal-overlay" onClick={onClose}>
      <div className="garage-modal garage-alert-modal" onClick={(e) => e.stopPropagation()}>
        <div className="garage-modal-header">
          <h2>{getTitle()}</h2>
          <button className="garage-modal-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="garage-modal-body garage-alert-body">
          <div className={`garage-alert-icon garage-alert-icon-${variant}`}>
            {getIcon()}
          </div>
          <p
            className={`garage-alert-message${
              typeof message === 'string' && message.includes('\n')
                ? ' garage-alert-message-multiline'
                : ''
            }`}
          >
            {typeof message === 'string' ? message : String(message ?? '')}
          </p>
        </div>

        <div className="garage-modal-actions">
          <button
            type="button"
            className="garage-btn garage-btn-primary"
            onClick={onClose}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </div>
  );

  // Render modal using React Portal at document.body level
  return ReactDOM.createPortal(modalContent, document.body);
};

export default AlertModal;
